import React, { useContext } from 'react';
import {
  ButtonIcon,
  ButtonTertiary,
  IconChevronDown,
  IconChevronUp,
  IconX,
} from '@spotify-internal/encore-web';
import { UserState } from './types';

import { RequireFeatureFlag } from '../../Auth/RequireFeatureFlag';
import {
  CloseSideMenuButton,
  StyledSideMenu,
  SideMenuAction,
  SidemenuSeparator,
  SideMenuLink,
  SideMenuCategory,
  SideMenuCategoryTitle,
  SideMenuCategoryItem,
} from './NavSideMenu.styles';
import { USE_SEARCH } from './constants';
import { isHelpLive } from '../../Help/util';
import { useT } from '@spotify-internal/i18n-core';
import { useRtl } from '../../../../features/src/i18n';
import { SideMenuLanguageSection } from './Language';
import { NavArrowIcon } from './utils';
import { FrodorModulesContext, Link } from '@spotify-internal/frodor-modules';
import { login, logout } from '../../../../features/src/auth';

const TRACK_CAT = 'Side Nav';

type NavSideMenuProps = {
  userState: UserState;
  onClose(event: any): void;
};

type MenuState = {
  features?: boolean;
  resources?: boolean;
  help?: boolean;
};

export const NavSideMenu: React.FC<NavSideMenuProps> = ({
  userState,
  onClose,
}) => {
  const t = useT();
  const rtl = useRtl();
  const { isFrodor, onLinkClick } = useContext(FrodorModulesContext);
  const getInternalHref = (href: string) =>
    isFrodor ? href : 'https://artists.spotify.com'.concat(href);

  const showLogin = userState === 'loggedOut';
  const showLogout = userState === 'claimed' || userState === 'unclaimed';
  const showDashboard = userState === 'claimed';
  const showClaim = userState === 'unclaimed' || userState === 'loggedOut';
  const [menuState, setMenuState] = React.useState<MenuState>({});

  const toggleMenu = (key: keyof MenuState) => {
    setMenuState(cur => ({ ...cur, [key]: !cur[key] }));
  };

  return (
    <StyledSideMenu
      dir={rtl ? 'rtl' : 'ltr'}
      id="nav-sidemenu"
      tabIndex={0}
      role="dialog"
      aria-label={t(
        'FRODOR_NAV_RESPONSIVE_LABEL',
        'Navigation dialog',
        'Label for responsive navigation dialog',
      )}
    >
      <CloseSideMenuButton
        tabIndex={0}
        id="close-sidemenu"
        onClick={onClose}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            onClose(e);
          }
        }}
        aria-label={t(
          'FRODOR_NAV_RESPONSIVE_CLOSE',
          'Close navigation menu',
          'close navigation menu in responsive display',
        )}
        iconOnly={IconX}
        condensedAll
      />

      <ul>
        {showClaim && (
          <li>
            <SideMenuAction>
              <Link
                dataTestId="navigation-sidemenu-claim"
                track={[TRACK_CAT, `Get Access (${userState})`]}
                href={getInternalHref('/claim')}
                useInternalNavigation={isFrodor}
              >
                {t('FRODOR_NAVBAR_SIDE_GET_ACCESS', 'Get access', '')}
              </Link>
            </SideMenuAction>
          </li>
        )}
        {showLogout && (
          <li>
            <SideMenuAction>
              <ButtonTertiary
                data-testid="navigation-sidemenu-logout"
                onClick={() => {
                  onLinkClick({
                    href: 'https://accounts.spotify.com/logout',
                    external: true,
                    trackingAttributes: [TRACK_CAT, 'Log In'],
                  });
                  logout();
                }}
              >
                {t('FRODOR_NAVBAR_SIDE_LOGOUT', 'Log out', '')}
              </ButtonTertiary>
            </SideMenuAction>
          </li>
        )}
        {showLogin && (
          <li>
            <SideMenuAction>
              <ButtonTertiary
                data-testid="navigation-sidemenu-login"
                onClick={() => {
                  onLinkClick({
                    href: 'https://accounts.spotify.com/login',
                    external: true,
                    trackingAttributes: [TRACK_CAT, 'Log In'],
                  });
                  login();
                }}
              >
                {t('FRODOR_NAVBAR_SIDE_LOGIN', 'Log in', '')}
              </ButtonTertiary>
            </SideMenuAction>
          </li>
        )}
        {showDashboard && (
          <li>
            <SideMenuAction>
              <Link
                track={[TRACK_CAT, 'Dashboard']}
                href={getInternalHref('/c')}
                dataTestId="navigation-sidemenu-dashboard"
                useInternalNavigation={isFrodor}
              >
                {t('FRODOR_NAVBAR_SIDE_DASHBOARD', 'Dashboard', '')}
              </Link>
            </SideMenuAction>
          </li>
        )}
        <li>
          <SidemenuSeparator />
        </li>

        <li>
          <SideMenuLink>
            <Link
              track={[TRACK_CAT, 'GetStarted']}
              href={getInternalHref('/get-started')}
              dataTestId="navigation-sidemenu-get-started"
              useInternalNavigation={isFrodor}
            >
              {t('FRODOR_NAVBAR_GET_STARTED', 'Get started', '')}
            </Link>
          </SideMenuLink>
        </li>

        {/* Features menu */}
        <li>
          <SideMenuCategory>
            <ButtonIcon onClick={() => toggleMenu('features')}>
              <SideMenuCategoryTitle down={!!menuState.features}>
                {t('FRODOR_NAVBAR_SIDE_FEATURES', 'Features', '')}
                {menuState.features ? (
                  <IconChevronUp
                    iconSize={24}
                    aria-label={t(
                      'FRODOR_NAVBAR_SIDE_FEATURES_CLOSE',
                      'Close features menu',
                      '',
                    )}
                  />
                ) : (
                  <IconChevronDown
                    iconSize={24}
                    aria-label={t(
                      'FRODOR_NAVBAR_SIDE_FEATURES_OPEN',
                      'Open features menu',
                      '',
                    )}
                  />
                )}
              </SideMenuCategoryTitle>
            </ButtonIcon>
            {menuState.features && (
              <ul>
                <li>
                  <SideMenuCategoryItem>
                    <Link
                      track={[TRACK_CAT, 'features-overview']}
                      href={getInternalHref('/features')}
                      dataTestId="navigation-sidemenu-overview"
                      useInternalNavigation={isFrodor}
                    >
                      {t('FRODOR_NAVBAR_FEATURES_OVERVIEW', 'Overview', '')}{' '}
                    </Link>
                  </SideMenuCategoryItem>
                </li>
                <li>
                  <SideMenuCategoryItem>
                    <Link
                      external
                      track={[TRACK_CAT, 'canvas']}
                      href="https://canvas.spotify.com?utm_campaign=owned_s4a_awr_all_clips_crossdevice__global-s4a-nav&utm_medium=owned&utm_source=s4a"
                      dataTestId="navigation-sidemenu-canvas"
                    >
                      Canvas <NavArrowIcon />
                    </Link>
                  </SideMenuCategoryItem>
                </li>
                <li>
                  <SideMenuCategoryItem>
                    <Link
                      external
                      track={[TRACK_CAT, 'clips']}
                      href="https://clips.byspotify.com?utm_campaign=owned_s4a_awr_all_clips_crossdevice__global-s4a-nav&utm_medium=owned&utm_source=s4a"
                      dataTestId="navigation-sidemenu-clips"
                    >
                      Clips <NavArrowIcon />
                    </Link>
                  </SideMenuCategoryItem>
                </li>
                <li>
                  <SideMenuCategoryItem>
                    <Link
                      track={[TRACK_CAT, 'discovery-mode']}
                      href={getInternalHref('/discovery-mode')}
                      dataTestId="navigation-sidemenu-discovery-mode"
                      useInternalNavigation={isFrodor}
                    >
                      Discovery Mode
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      dataTestId="navigation-sidemenu-marquee"
                      track={[TRACK_CAT, 'Marquee']}
                      href={getInternalHref('/marquee')}
                      useInternalNavigation={isFrodor}
                    >
                      Marquee
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      dataTestId="navigation-sidemenu-merch"
                      track={[TRACK_CAT, 'Merch']}
                      href={getInternalHref('/merch')}
                      useInternalNavigation={isFrodor}
                    >
                      {t('FRODOR_NAVBAR_MERCH_LINK', 'Merch', '')}
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      dataTestId="navigation-sidemenu-playlisting"
                      track={[TRACK_CAT, 'Playlisting']}
                      href={getInternalHref('/playlisting')}
                      useInternalNavigation={isFrodor}
                    >
                      {t('FRODOR_NAVBAR_PLAYLISTING_LINK', 'Playlisting', '')}
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      external
                      dataTestId="navigation-promocards"
                      track={[TRACK_CAT, 'Promo Cards']}
                      href="https://promocards.byspotify.com/artists"
                    >
                      {t(
                        'FRODOR_NAVBAR_SIDE_PROMO_CARDS',
                        'Promo Cards',
                        'Promo is short for promotional',
                      )}{' '}
                      <NavArrowIcon />
                    </Link>
                  </SideMenuCategoryItem>
                </li>
              </ul>
            )}
          </SideMenuCategory>
        </li>

        {/* Resources menu */}
        <li>
          <SideMenuCategory>
            <ButtonIcon onClick={() => toggleMenu('resources')}>
              <SideMenuCategoryTitle down={!!menuState.resources}>
                {t('FRODOR_NAVBAR_SIDE_RESOURCES_MENU', 'Resources', '')}
                {menuState.resources ? (
                  <IconChevronUp
                    iconSize={24}
                    aria-label={t(
                      'FRODOR_NAVBAR_SIDE_RESOURCES_MENU_CLOSE',
                      'Close resources menu',
                      '',
                    )}
                  />
                ) : (
                  <IconChevronDown
                    iconSize={24}
                    aria-label={t(
                      'FRODOR_NAVBAR_SIDE_RESOURCES_MENU_OPEN',
                      'Open resources menu',
                      '',
                    )}
                  />
                )}
              </SideMenuCategoryTitle>
            </ButtonIcon>
            {menuState.resources && (
              <ul>
                <li>
                  <SideMenuCategoryItem>
                    <Link
                      track={[TRACK_CAT, 'Videos']}
                      href={getInternalHref('/videos')}
                      dataTestId="navigation-sidemenu-videos"
                      useInternalNavigation={isFrodor}
                    >
                      {t('FRODOR_NAVBAR_SIDE_VIDEOS', 'Videos', '')}
                    </Link>
                  </SideMenuCategoryItem>
                </li>
                <li>
                  <SideMenuCategoryItem>
                    <Link
                      track={[TRACK_CAT, 'Blog']}
                      href={getInternalHref('/blog')}
                      dataTestId="navigation-sidemenu-blog"
                      useInternalNavigation={isFrodor}
                    >
                      {t('FRODOR_NAVBAR_SIDE_NEWS', 'News', '')}
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      track={[TRACK_CAT, 'Events']}
                      href={getInternalHref('/events')}
                      dataTestId="navigation-sidemenu-events"
                      useInternalNavigation={isFrodor}
                    >
                      {t('FRODOR_NAVBAR_SIDE_EVENTS', 'Events', '')}
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      external
                      dataTestId="navigation-infocus"
                      track={[TRACK_CAT, 'In Focus']}
                      href="https://infocus.byspotify.com?utm_campaign=side-nav&utm_medium=owned-product&utm_source=s4a"
                    >
                      In Focus <NavArrowIcon />
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      external
                      dataTestId="navigation-fanstudy"
                      track={[TRACK_CAT, 'Fan Study']}
                      href="https://fanstudy.byspotify.com?utm_campaign=side-nav&utm_medium=owned-product&utm_source=s4a"
                    >
                      Fan Study <NavArrowIcon />
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      external
                      dataTestId="navigation-madetobefound"
                      track={[TRACK_CAT, 'Made to Be Found']}
                      href="https://found.byspotify.com?utm_campaign=side-nav&utm_medium=owned-product&utm_source=s4a"
                    >
                      Made to Be Found <NavArrowIcon />
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      external
                      dataTestId="navigation-loudclear"
                      track={[TRACK_CAT, 'Loud & Clear']}
                      href="https://loudandclear.byspotify.com?utm_campaign=side-nav&utm_medium=owned-product&utm_source=s4a"
                    >
                      Loud & Clear <NavArrowIcon />
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      external
                      dataTestId="navigation-noteable"
                      track={[TRACK_CAT, 'Noteable']}
                      href="https://noteable.spotify.com?utm_campaign=side-nav&utm_medium=owned-product&utm_source=s4a"
                    >
                      Noteable <NavArrowIcon />
                    </Link>
                  </SideMenuCategoryItem>
                </li>
              </ul>
            )}
          </SideMenuCategory>
        </li>

        {/* Help link */}

        {isHelpLive && (
          <li>
            <SideMenuLink>
              <Link
                track={[TRACK_CAT, 'Help']}
                href={getInternalHref('/help')}
                dataTestId="navigation-sidemenu-help"
                useInternalNavigation={isFrodor}
              >
                {t('FRODOR_NAVBAR_SIDE_HELP_LIVE', 'Help', '')}
              </Link>
            </SideMenuLink>
          </li>
        )}

        {!isHelpLive && (
          <li>
            <SideMenuCategory>
              <ButtonIcon onClick={() => toggleMenu('help')}>
                <SideMenuCategoryTitle down={!!menuState.help}>
                  {t('FRODOR_NAVBAR_SIDE_HELP_NOT_LIVE', 'Help', '')}
                  {menuState.help ? (
                    <IconChevronUp
                      iconSize={24}
                      aria-label={t(
                        'FRODOR_NAVBAR_SIDE_CLOSE_HELP',
                        'Close help menu',
                        '',
                      )}
                    />
                  ) : (
                    <IconChevronDown
                      iconSize={24}
                      aria-label={t(
                        'FRODOR_NAVBAR_SIDE_OPEN_HELP',
                        'Open help menu',
                        '',
                      )}
                    />
                  )}
                </SideMenuCategoryTitle>
              </ButtonIcon>
              {menuState.help && (
                <ul>
                  <li>
                    <SideMenuCategoryItem>
                      <Link
                        track={[TRACK_CAT, 'Guide']}
                        href={getInternalHref('/guide')}
                        dataTestId="navigation-sidemenu-guide"
                        useInternalNavigation={isFrodor}
                      >
                        {t('FRODOR_NAVBAR_SIDE_GUIDE', 'Guide', '')}
                      </Link>
                    </SideMenuCategoryItem>
                  </li>
                  <li>
                    <SideMenuCategoryItem>
                      <Link
                        track={[TRACK_CAT, 'FAQs']}
                        href={getInternalHref('/faq')}
                        dataTestId="navigation-sidemenu-faq"
                        useInternalNavigation={isFrodor}
                      >
                        FAQ
                      </Link>
                    </SideMenuCategoryItem>
                  </li>
                </ul>
              )}
            </SideMenuCategory>
          </li>
        )}

        {USE_SEARCH && (
          <RequireFeatureFlag
            flag="s4a-frodor-test"
            fallback={<></>}
            waiting={<></>}
          >
            <li>
              <SideMenuLink>
                <Link
                  track={[TRACK_CAT, 'Search']}
                  href={getInternalHref('/search')}
                  dataTestId="navigation-sidemenu-search"
                  useInternalNavigation={isFrodor}
                >
                  {t('FRODOR_NAVBAR_SIDE_SEARCH', 'Search', '')}
                </Link>
              </SideMenuLink>
            </li>
          </RequireFeatureFlag>
        )}
      </ul>
      <SideMenuLanguageSection onButtonClick={onClose} />
    </StyledSideMenu>
  );
};
